/**
 * MerakiOnboardingPage — guided multi-step network setup.
 *
 * Checks 4 compliance areas against the GuestTek baseline. Each step can be
 * re-applied individually or all pending steps run at once.
 */
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  PlayCircle,
  RotateCw,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Stepper } from "@/components/ui/stepper";
import { NetworkPicker } from "@/components/app/network-picker";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import { pollCeleryTask } from "@/api/celery";
import { OnboardingStatusSchema, type MerakiNetwork, type OnboardingStepStatus } from "@/api/schemas/meraki";

/** Visual treatment per step state. */
function stateStyle(s: OnboardingStepStatus["state"]): {
  pill: "ok" | "warn" | "err" | "info";
  icon: React.ReactNode;
  label: string;
} {
  switch (s) {
    case "ok":
      return { pill: "ok", label: "compliant", icon: <CheckCircle2 size={14} /> };
    case "running":
      return { pill: "info", label: "running", icon: <Loader2 size={14} className="animate-spin" /> };
    case "error":
      return { pill: "err", label: "non-compliant", icon: <XCircle size={14} /> };
    default:
      return { pill: "warn", label: "pending", icon: <Clock size={14} /> };
  }
}

// ─── Apply-progress reducer ───────────────────────────────────────────────────

type ApplyState = {
  open: boolean;
  slug: string | null;
  pct: number | undefined;
  msg: string;
  done: boolean;
};

type ApplyAction =
  | { type: "start"; slug: string }
  | { type: "progress"; pct?: number; msg?: string }
  | { type: "done" }
  | { type: "close" };

function applyReducer(state: ApplyState, action: ApplyAction): ApplyState {
  switch (action.type) {
    case "start":
      return { open: true, slug: action.slug, pct: undefined, msg: "Queueing…", done: false };
    case "progress":
      return { ...state, pct: action.pct ?? state.pct, msg: action.msg ?? state.msg };
    case "done":
      return { ...state, done: true };
    case "close":
      return { ...state, open: false };
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ApplyProgressDialogProps {
  state: ApplyState;
  onOpenChange: (open: boolean) => void;
}

function ApplyProgressDialog({ state, onOpenChange }: ApplyProgressDialogProps) {
  return (
    <Dialog open={state.open} onOpenChange={onOpenChange}>
      <DialogContent>
        {state.done ? (
          <div className="flex flex-col items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
              <CheckCircle2 size={22} />
            </div>
            <DialogTitle>Applied successfully</DialogTitle>
            <DialogDescription>
              Settings have been pushed. Closing in a moment…
            </DialogDescription>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <DialogTitle>Applying · {state.slug}</DialogTitle>
            <DialogDescription>{state.msg}</DialogDescription>
            <Progress value={state.pct} hint={state.pct ? undefined : "Contacting Meraki…"} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface OnboardingStepListProps {
  steps: OnboardingStepStatus[];
  onApply: (slug: string) => void;
}

function OnboardingStepList({ steps, onApply }: OnboardingStepListProps) {
  return (
    <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
      {steps.map((s) => {
        const sty = stateStyle(s.state);
        const isRunning = s.state === "running";
        return (
          <li
            key={s.slug}
            className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 rounded-xl border border-line bg-surface p-3"
          >
            <span
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${
                sty.pill === "ok"
                  ? "bg-mint/15 text-mint"
                  : sty.pill === "info"
                    ? "bg-violet/15 text-violet"
                    : sty.pill === "err"
                      ? "bg-rose/15 text-rose"
                      : "bg-peach/15 text-peach"
              }`}
            >
              {sty.icon}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-fg">{s.title}</div>
              {s.detail ? (
                <div className="text-[11px] text-fg-mute">{s.detail}</div>
              ) : null}
            </div>
            <Badge variant={sty.pill} size="sm">{sty.label}</Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onApply(s.slug)}
              disabled={isRunning || s.state === "ok"}
            >
              <RotateCw size={13} />
              {isRunning ? "…" : "Apply"}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function MerakiOnboardingPage() {
  const [network, setNetwork] = useState<MerakiNetwork | null>(null);
  const [steps, setSteps] = useState<OnboardingStepStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyDialog, dispatchApply] = useReducer(applyReducer, {
    open: false, slug: null, pct: undefined, msg: "Starting…", done: false,
  });

  const loadStatus = useCallback(async (net: MerakiNetwork) => {
    setLoading(true);
    setSteps([]);
    try {
      const raw = await apiGet<unknown>(
        `/meraki/onboarding/status/?network_id=${net.id}`,
      );
      const parsed = OnboardingStatusSchema.safeParse(raw);
      if (parsed.success) {
        setSteps(parsed.data);
      } else {
        toast.error("Unexpected response from onboarding status endpoint");
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Could not load onboarding status", { description: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (network) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadStatus(network);
    } else {
      setSteps([]);
    }
  }, [network, loadStatus]);

  const activeIndex = useMemo(() => {
    const i = steps.findIndex((s) => s.state === "running" || s.state === "error" || s.state === "pending");
    return i === -1 ? steps.length - 1 : i;
  }, [steps]);

  const allOk = steps.length > 0 && steps.every((s) => s.state === "ok");

  async function applyStep(slug: string) {
    if (!network) return;
    dispatchApply({ type: "start", slug });

    // Mark step as running locally while the task runs.
    setSteps((prev) =>
      prev.map((s) => (s.slug === slug ? { ...s, state: "running", detail: "applying…" } : s)),
    );

    try {
      const res = await apiPost<{ taskId: string }>("/meraki/onboarding/apply/", {
        network_id: network.id,
        slug,
      });

      dispatchApply({ type: "progress", msg: "Applying compliance settings…" });
      const final = await pollCeleryTask(res.taskId, (p) => {
        dispatchApply({
          type: "progress",
          pct: p.total && p.current !== undefined ? (p.current / p.total) * 100 : undefined,
          msg: p.message || undefined,
        });
      });

      if (final.state === "SUCCESS") {
        dispatchApply({ type: "done" });
        setTimeout(() => dispatchApply({ type: "close" }), 1400);
        toast.success("Step applied", {
          description: `${slug} settings have been pushed to ${network.name}.`,
        });
        // Re-fetch status so the stepper reflects the real state.
        void loadStatus(network);
      } else {
        toast.error("Apply failed", { description: final.message ?? "Unknown error" });
        setSteps((prev) =>
          prev.map((s) =>
            s.slug === slug ? { ...s, state: "error", detail: final.message ?? "apply failed" } : s,
          ),
        );
        dispatchApply({ type: "close" });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Could not apply step", { description: msg });
      setSteps((prev) =>
        prev.map((s) => (s.slug === slug ? { ...s, state: "error", detail: msg } : s)),
      );
      dispatchApply({ type: "close" });
    }
  }

  function runAll() {
    if (!network) return;
    const pending = steps.filter((s) => s.state === "pending" || s.state === "error");
    if (pending.length === 0) {
      toast.info("All steps are already compliant");
      return;
    }
    // Apply sequentially — fire first, the rest follow the re-fetch.
    void applyStep(pending[0].slug);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">Meraki</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Onboarding</span>
      </div>

      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Onboarding</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Provisions a new or existing hotel with the standard GuestTek baseline. Each step is
            idempotent — safe to re-run.
          </p>
        </div>
        {allOk && network ? (
          <Badge variant="ok" size="md">
            <CheckCircle2 size={11} />
            Fully compliant
          </Badge>
        ) : null}
      </div>

      <Card>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle>Network</CardTitle>
            <CardSub className="mt-1">Pick the network to check and onboard.</CardSub>
            <div className="mt-3">
              <NetworkPicker value={network} onChange={setNetwork} />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button onClick={runAll} disabled={!network || loading || allOk}>
              <PlayCircle size={14} />
              Fix all issues
            </Button>
            {network && !loading ? (
              <button
                type="button"
                onClick={() => void loadStatus(network)}
                className="flex items-center gap-1 text-xs text-fg-mute hover:text-fg"
              >
                <RotateCw size={11} />
                Refresh
              </button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {network ? (
        loading ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <Loader2 size={24} className="animate-spin text-violet" />
              <CardSub>Checking compliance for {network.name}…</CardSub>
            </CardContent>
          </Card>
        ) : steps.length > 0 ? (
          <>
            <Card>
              <CardContent>
                <Stepper
                  active={activeIndex}
                  steps={steps.map((s) => ({ id: s.slug, title: s.title, hint: s.detail }))}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Compliance status</CardTitle>
                    <CardSub className="mt-1">
                      {steps.filter((s) => s.state === "ok").length}/{steps.length} areas compliant
                    </CardSub>
                  </div>
                </div>
                <OnboardingStepList steps={steps} onApply={(slug) => void applyStep(slug)} />
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <AlertTriangle size={20} className="text-peach" />
              <CardTitle>No compliance data</CardTitle>
              <CardSub>The network may not support all compliance checks.</CardSub>
            </CardContent>
          </Card>
        )
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CardTitle>Pick a network to begin</CardTitle>
            <CardSub>Compliance status will populate as soon as you select one.</CardSub>
          </CardContent>
        </Card>
      )}

      <ApplyProgressDialog
        state={applyDialog}
        onOpenChange={(open) => { if (!open) dispatchApply({ type: "close" }); }}
      />
    </div>
  );
}
